import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bidService } from '../services/bidService';
import { setBids } from '../store/slices/bidsSlice';
import type { RootState } from '../store';
import { BidCard } from '../components/BidCard';
import { SubscriptionGateModal } from '../components/SubscriptionGateModal';
import { BidStatus } from '../types';
import type { Bid } from '../types';
import {
  getLiveBidSubscriptionBlockMessage,
  shouldBlockLiveBidEntry,
} from '../utils/subscriptionAccess';

export function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const [gateMessage, setGateMessage] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bids'],
    queryFn: bidService.getBids,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const bids = query.state.data ?? [];
      const arr = Array.isArray(bids) ? bids : [];
      if (arr.some((b: Bid) => b.status === BidStatus.UPCOMING)) return 5000;
      if (arr.some((b: Bid) => b.status === BidStatus.LIVE)) return 5000;
      return false;
    },
  });

  useEffect(() => {
    if (data) dispatch(setBids(data));
  }, [data, dispatch]);

  const list = data ?? [];
  const live = list.filter((b) => b.status === BidStatus.LIVE);
  const upcoming = list.filter((b) => b.status === BidStatus.UPCOMING);

  const handleNavigateToBid = (bid: Bid) => {
    if (shouldBlockLiveBidEntry(bid, user?.id, user)) {
      const msg = getLiveBidSubscriptionBlockMessage(user);
      if (msg) setGateMessage(msg);
      return;
    }
    navigate(`/bid/${bid.id}`);
  };

  return (
    <div>
      <h1 className="text-xl font-black text-slate-900 mb-6">Home</h1>

      {isLoading && list.length === 0 ? (
        <p className="text-slate-500 text-center py-12">Loading bids...</p>
      ) : (
        <>
          <section className="mb-8">
            <h2 className="text-sm font-bold text-slate-800 mb-3">Live Bid</h2>
            {live.length ? (
              live.map((b) => (
                <BidCard
                  key={b.id}
                  bid={b}
                  currentUserId={user?.id}
                  onEdit={(bid) => navigate(`/create/${bid.id}`)}
                  onUpcomingExpire={refetch}
                  onLiveExpire={refetch}
                  onNavigateToBid={handleNavigateToBid}
                />
              ))
            ) : (
              <p className="text-slate-400 text-sm italic">No live auctions right now.</p>
            )}
          </section>
          <section>
            <h2 className="text-sm font-bold text-slate-800 mb-3">Upcoming Bid</h2>
            {upcoming.length ? (
              upcoming.map((b) => (
                <BidCard
                  key={b.id}
                  bid={b}
                  currentUserId={user?.id}
                  onEdit={(bid) => navigate(`/create/${bid.id}`)}
                  onUpcomingExpire={refetch}
                  onNavigateToBid={handleNavigateToBid}
                />
              ))
            ) : (
              <p className="text-slate-400 text-sm italic">No upcoming auctions scheduled.</p>
            )}
          </section>
        </>
      )}

      {gateMessage ? (
        <SubscriptionGateModal
          message={gateMessage}
          onOk={() => {
            setGateMessage(null);
            navigate('/subscription');
          }}
        />
      ) : null}
    </div>
  );
}
